import {
  buildActionQueue,
  buildAttentionBlocks,
  buildBlockPerformanceActionQueue,
  buildBlockPerformanceClosurePlan,
  buildBlockPerformanceFocus,
  buildBlockPerformanceNarrative,
  buildClosurePlan,
  buildDataEntryDefaultPriorityActions,
  buildDataEntryDefaultRecommendations,
  buildDataEntryDefaultSignals,
  buildDataEntryInsightTimeline,
  buildDataEntryInsightsMarkdown,
  buildHeroNarrative,
  buildPrimaryAction,
  buildQualityAuditActionQueue,
  buildQualityAuditClosurePlan,
  buildQualityAuditNarrative,
  buildQualityAuditPrimaryAction,
  buildQualityMetricDescription,
  buildSchoolMonitoringActionQueue,
  buildSchoolMonitoringClosurePlan,
  buildSchoolMonitoringInsightText,
  buildSchoolMonitoringNarrative,
  buildSchoolMonitoringPrimaryAction,
  getCertificationCoverageBlocks,
  getEntryStabilityChip,
  getTopPendingBlocks,
  getTopRepeaterBlocks,
} from "@/utils/dataEntryDerived";

export const buildDataEntryLocalGuidance = ({
  overview,
  blockData,
  completionBands,
  criticalCount,
  highRepeaterSchools,
  dataQuality,
  certificationData,
  topBottomBlocks,
}) => {
  if (!overview) return null;

  const attentionBlocks = buildAttentionBlocks(blockData, 5);
  const topRepeaterBlocks = getTopRepeaterBlocks(blockData, 5);
  const topPendingBlocks = getTopPendingBlocks(blockData, 5);
  const certCoverageBlocks = getCertificationCoverageBlocks(blockData, 6);
  const highRepeaterCount = highRepeaterSchools?.length ?? 0;
  const certRate = certificationData?.certification_rate ?? overview?.certification_rate ?? 0;
  const nonCertSchools =
    certificationData?.non_certified?.schools ?? overview?.non_certified_schools ?? 0;
  const stability = getEntryStabilityChip(overview);

  return {
    executive: {
      hero_narrative: buildHeroNarrative(overview),
      primary_action: buildPrimaryAction(overview),
      action_queue: buildActionQueue(overview, attentionBlocks),
      closure_plan: buildClosurePlan(attentionBlocks),
    },
    blocks: {
      narrative: buildBlockPerformanceNarrative(overview),
      primary_focus: buildBlockPerformanceFocus(topRepeaterBlocks, topPendingBlocks),
      entry_stability_label: stability.label,
      action_queue: buildBlockPerformanceActionQueue(overview, topRepeaterBlocks, topPendingBlocks),
      closure_plan: buildBlockPerformanceClosurePlan(overview),
    },
    schools: {
      narrative: buildSchoolMonitoringNarrative(overview, criticalCount, highRepeaterCount),
      primary_action: buildSchoolMonitoringPrimaryAction(overview, criticalCount, certRate),
      insight_cards: [
        {
          type: "critical",
          text: buildSchoolMonitoringInsightText("critical", overview, criticalCount, highRepeaterCount, certRate),
        },
        {
          type: "repeater",
          text: buildSchoolMonitoringInsightText("repeater", overview, criticalCount, highRepeaterCount, certRate),
        },
        {
          type: "cert",
          text: buildSchoolMonitoringInsightText("cert", overview, criticalCount, highRepeaterCount, certRate),
        },
        {
          type: "completion",
          text: buildSchoolMonitoringInsightText("completion", overview, criticalCount, highRepeaterCount, certRate),
        },
      ],
      action_queue: buildSchoolMonitoringActionQueue(overview, criticalCount, highRepeaterCount),
      closure_plan: buildSchoolMonitoringClosurePlan(overview, criticalCount, highRepeaterCount),
    },
    quality: {
      narrative: buildQualityAuditNarrative(overview, dataQuality, certRate, nonCertSchools),
      primary_action: buildQualityAuditPrimaryAction(overview, certCoverageBlocks),
      metric_descriptions: {
        consistency: buildQualityMetricDescription("consistency", dataQuality, overview),
        mismatch: buildQualityMetricDescription("mismatch", dataQuality, overview),
        zero_entry: buildQualityMetricDescription("zero_entry", dataQuality, overview),
        full_entry: buildQualityMetricDescription("full_entry", dataQuality, overview),
      },
      action_queue: buildQualityAuditActionQueue(overview, certificationData, dataQuality, blockData),
      closure_plan: buildQualityAuditClosurePlan(certCoverageBlocks),
    },
    insights: {
      signals: buildDataEntryDefaultSignals(overview, criticalCount, blockData),
      recommendations: buildDataEntryDefaultRecommendations(overview, criticalCount, blockData),
      priority_actions: buildDataEntryDefaultPriorityActions(overview, criticalCount),
      timeline: buildDataEntryInsightTimeline(overview, criticalCount, blockData),
      markdown: buildDataEntryInsightsMarkdown(overview, criticalCount, blockData),
    },
  };
};

export const buildDataEntryMetricsPayload = ({
  overview,
  blockData,
  completionBands,
  criticalCount,
  criticalSchools,
  highRepeaterSchools,
  dataQuality,
  certificationData,
  topBottomBlocks,
  scopeLabel,
}) => ({
  scope_label: scopeLabel,
  overview,
  completion_bands: completionBands,
  critical_count: criticalCount,
  critical_schools_sample: (criticalSchools ?? []).slice(0, 10),
  high_repeater_count: (highRepeaterSchools ?? []).length,
  data_quality: dataQuality,
  certification: certificationData,
  worst_block: topBottomBlocks?.worst_block,
  block_summary: (blockData ?? []).slice(0, 12).map((b) => ({
    block_name: b.block_name,
    completion_pct: b.completion_pct ?? b.completion_rate,
    pending_students: b.pending_students,
    certification_rate: b.certification_rate,
    total_repeaters: b.total_repeaters,
  })),
});
